import { supabase, supabaseConfig } from './supabase';
import { getLeadId, getLeadSessionKey } from './leadCapture';

export interface ServiceSubscriptionAvailability {
  service_slug: string;
  service_name: string;
  subscription_enabled: boolean;
  unavailable_message?: string | null;
}

export const SERVICE_TYPE_TO_SUBSCRIPTION_SLUG: Record<string, string> = {
  'repeat-referral-system': 'repeat_referral',
  'content-engine': 'content_engine',
  'proof-of-work': 'jobs_to_socials',
  'nap-entity-boost': 'entity_system',
  'ai-audit': 'ai_audit',
  'seo-gbp-audit': 'analytics_reporting',
  'customer-intelligence': 'customer_intelligence',
};

export function availabilitySlugForServiceType(serviceTypeId: string) {
  return SERVICE_TYPE_TO_SUBSCRIPTION_SLUG[serviceTypeId] || '';
}

export async function loadServiceSubscriptionAvailability(): Promise<ServiceSubscriptionAvailability[]> {
  if (!supabaseConfig.isConfigured) return [];
  const { data, error } = await supabase.rpc('get_public_service_subscription_availability');
  if (error) throw error;
  return (data || []) as ServiceSubscriptionAvailability[];
}

export function serviceIsUnavailable(
  rows: ServiceSubscriptionAvailability[],
  serviceTypeId: string,
) {
  const slug = availabilitySlugForServiceType(serviceTypeId);
  return rows.find((row) => row.service_slug === slug)?.subscription_enabled === false;
}

export function serviceUnavailableMessage(
  rows: ServiceSubscriptionAvailability[],
  serviceTypeId: string,
) {
  const slug = availabilitySlugForServiceType(serviceTypeId);
  return rows.find((row) => row.service_slug === slug)?.unavailable_message
    || 'Currently unavailable. Click to be notified when it is available.';
}

export async function recordServiceAvailabilityInterest(input: {
  serviceTypeId?: string;
  serviceSlug?: string;
  serviceName?: string;
  email?: string | null;
  phone?: string | null;
  contactName?: string | null;
  businessName?: string | null;
  textNotificationsAllowed?: boolean;
}) {
  const serviceSlug = input.serviceSlug || availabilitySlugForServiceType(input.serviceTypeId || '');
  if (!serviceSlug) throw new Error('Service is not recognized.');
  if (!supabaseConfig.isConfigured) return { ok: false, skipped: true };

  const { data, error } = await supabase.functions.invoke('onboarding-lead-capture', {
    body: {
      action: 'service_availability_interest',
      session_key: getLeadSessionKey(),
      lead_id: getLeadId(),
      service_slug: serviceSlug,
      service_type: input.serviceTypeId,
      service_name: input.serviceName,
      email: input.email,
      phone: input.phone,
      contact_name: input.contactName,
      business_name: input.businessName,
      text_notifications_allowed: input.textNotificationsAllowed === true,
      source: 'onboarding',
    },
  });
  if (error) throw error;
  return data;
}
